// Typed client wrappers + hooks for the P2 WhatsApp-core callables (PRD §4.3,
// §4.4). The Connect and Templates pages consume these to replace their mock
// data — no Firestore writes from the client (CLAUDE.md rule 2); everything
// goes through the tenant-scoped callables.
import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { stripUndefined } from './callable';
import type {
  TplComponent,
  WaTemplateHeaderMedia,
  WaTemplateVariable,
  WaTemplateVariableSource,
} from './templateTypes';

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => (await fn(stripUndefined(data))).data;
}

// ── Integration (Connect WhatsApp) ───────────────────────────────────────────
export interface NumberHealthResult {
  qualityRating: 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN' | string | null;
  messagingLimitTier: 'TIER_1K' | 'TIER_10K' | 'TIER_100K' | 'TIER_UNLIMITED' | string | null;
  nameStatus: 'APPROVED' | 'AVAILABLE_WITHOUT_REVIEW' | 'DECLINED' | 'EXPIRED' | 'PENDING_REVIEW' | string | null;
  codeVerificationStatus: 'VERIFIED' | 'NOT_VERIFIED' | string | null;
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  platformType: string | null;
  lastSyncAt: number;
  tokenExpiresAt?: number | null;
  error?: string | null;
}

export interface ConnectSummary {
  businessAccounts: number;
  phoneNumbers: number;
  expiringSoon: number;
  disconnected: number;
}

export interface IntegrationView {
  configured?: boolean;
  id?: string;
  phoneNumberId?: string;
  businessAccountId?: string;
  verifiedPhoneE164?: string | null;
  verifyToken?: string;
  isLive?: boolean;
  isPaused?: boolean;
  hasToken?: boolean;
  webhookUrl?: string;
  updatedAt?: number;
  /** Multi-number fields. */
  label?: string | null;
  coexistence?: boolean;
  onboarding?: 'manual' | 'embedded_signup_cloud_api' | 'embedded_signup_business_app';
  registeredAt?: number | null;
  historySync?: { status: string; progress: number; error?: string | null } | null;
  health?: NumberHealthResult | null;
  lastHealthError?: string | null;
  catalogId?: string | null;
  nameStatus?: string | null;
  pendingDisplayName?: string | null;
  nameRequestedAt?: number | null;
}

/** A connected number as listed in the account switcher. `id` is always set. */
export type ConnectedNumber = IntegrationView & { id: string; phoneNumberId: string };

export const saveIntegration = call<
  { phoneNumberId: string; businessAccountId: string; token?: string; appSecret?: string; integrationId?: string; label?: string },
  IntegrationView
>('whatsappSaveIntegration');

/** One number — a specific one, or the tenant's default when `integrationId` is omitted. */
export const getIntegration = call<{ integrationId?: string }, IntegrationView>('whatsappGetIntegration');

export const listIntegrations = call<Record<string, never>, { items: ConnectedNumber[]; defaultId: string | null }>('whatsappListIntegrations');
export const setDefaultIntegration = call<{ integrationId: string }, { ok: boolean }>('whatsappSetDefaultIntegration');
export const removeIntegration = call<{ integrationId: string }, { ok: boolean; remaining: number }>('whatsappRemoveIntegration');

// WP-06: Connect page v2 callables
export const whatsappConnectSummary = call<
  Record<string, never>,
  { ok: boolean; summary: ConnectSummary; integrations: ConnectedNumber[] }
>('whatsappConnectSummary');

export const whatsappNumberHealth = call<
  { integrationId?: string },
  { ok: boolean; health?: NumberHealthResult; items?: Array<{ integrationId: string; health: NumberHealthResult }> }
>('whatsappNumberHealth');

export const whatsappSetIntegrationPaused = call<
  { integrationId: string; paused: boolean },
  { ok: boolean; isPaused: boolean }
>('whatsappSetIntegrationPaused');

// WP-21 / WP-21b: Display name change request & status reconciliation
export const whatsappRequestDisplayName = call<
  { integrationId: string; newDisplayName: string },
  { ok: boolean; nameStatus?: string; pendingDisplayName?: string }
>('whatsappRequestDisplayName');

export const whatsappGetDisplayNameStatus = call<
  { integrationId: string },
  {
    ok: boolean;
    currentDisplayName?: string | null;
    nameStatus?: string | null;
    newDisplayNameStatus?: string | null;
    pendingDisplayName?: string | null;
    verifiedName?: string | null;
    newNameStatus?: string | null;
    nameRequestedAt?: number | null;
  }
>('whatsappGetDisplayNameStatus');

/** Display name for a number: its label, else its phone, else its id. */
export function numberLabel(n: Pick<IntegrationView, 'label' | 'verifiedPhoneE164' | 'phoneNumberId' | 'id'> | null | undefined): string {
  if (!n) return '';
  return n.label || n.verifiedPhoneE164 || (n.phoneNumberId ? `#${n.phoneNumberId.slice(-6)}` : n.id ?? '');
}

export const requestNumberCode = call<
  { phoneE164: string; codeMethod?: 'SMS' | 'VOICE'; integrationId?: string },
  { sandbox: boolean; requestedAt: number }
>('whatsappRequestNumberCode');

export const confirmNumberCode = call<
  { code: string; integrationId?: string },
  { sandbox: boolean; verifiedAt: number; verifiedPhoneE164: string | null }
>('whatsappConfirmNumberCode');

// ── Templates ────────────────────────────────────────────────────────────────
export interface TemplateView {
  id: string;
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: string;
  body: string;
  variablesCount: number;
  components?: TplComponent[];
  variables?: WaTemplateVariable[];
  headerMedia?: WaTemplateHeaderMedia | null;
  qualityScore?: string | null;
  metaTemplateId?: string | null;
  submittedAt: number;
  updatedAt?: number;
  /** Which WABA / number the template was submitted through (multi-number). */
  wabaId?: string | null;
  integrationId?: string | null;
}

export interface SubmitTemplateInput {
  name: string;
  language: string;
  category: TemplateView['category'];
  body?: string;
  variablesCount?: number;
  variables?: Array<{
    index: number;
    source: WaTemplateVariableSource;
    example: string;
  }>;
  components?: TplComponent[];
  headerMedia?: {
    handle: string;
    mime: string;
    size: number;
    storagePath: string;
    createdAt: number;
  };
  integrationId?: string;
}

export const submitTemplate = call<
  SubmitTemplateInput,
  { id: string; status: string; sandbox: boolean }
>('whatsappSubmitTemplate');

export const listTemplates = call<Record<string, never>, { items: TemplateView[] }>('whatsappListTemplates');

export const templateStatus = call<
  { name: string; language: string; integrationId?: string },
  { found: boolean; status?: string; metaTemplateId?: string | null }
>('whatsappTemplateStatus');

export const uploadTemplateMedia = call<
  {
    storagePath: string;
    mimeType: string;
    fileSize?: number;
    templateId?: string;
    integrationId?: string;
  },
  {
    handle: string;
    header_handle: string;
    mime: string;
    size: number;
    storagePath: string;
    createdAt: number;
    sandbox: boolean;
  }
>('whatsappUploadTemplateMedia');

export const importTemplates = call<
  { integrationId?: string },
  { imported: number; total: number; failures?: string[] }
>('whatsappImportTemplates');

export const rewriteTemplateBody = call<
  {
    body: string;
    tone?: 'persuasive' | 'urgent' | 'friendly' | 'professional' | 'concise';
    language?: string;
  },
  { variants: string[] }
>('whatsappRewriteTemplateBody');

// ── Send (inbox reply) ───────────────────────────────────────────────────────
export const sendMessage = call<
  {
    to: string;
    conversationId?: string;
    body?: string;
    templateName?: string;
    templateLanguage?: string;
    templateVariables?: string[];
    /** Send-from number for a NEW conversation; replies always use the conversation's own number. */
    integrationId?: string;
    /** WP-04: outbound media (id obtained from mediaUpload) + caption/document flag. */
    mediaId?: string;
    mediaUrl?: string;
    mediaType?: 'image' | 'video' | 'audio' | 'document';
    caption?: string;
    asDocument?: boolean;
    /** WP-04: an internal note is stored but never sent to Meta. */
    noteText?: string;
    direction?: 'out' | 'note';
    /** WP-21b / WP-22: Interactive single product message. */
    product?: {
      catalogId: string;
      retailerId: string;
      bodyText?: string;
      footerText?: string;
    };
    /** WP-21b / WP-22: Interactive multi-product list message. */
    productList?: {
      catalogId: string;
      headerText?: string;
      bodyText: string;
      footerText?: string;
      sections: Array<{
        title: string;
        productRetailerIds: string[];
      }>;
    };
  },
  { sandbox: boolean; messageId: string; conversationId: string; status: string; providerRef: string | null }
>('whatsappSend');

// ── Hooks ────────────────────────────────────────────────────────────────────
export function useIntegration(integrationId?: string | null) {
  const [data, setData] = useState<IntegrationView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getIntegration(integrationId ? { integrationId } : {}));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

export function useTemplates() {
  const [items, setItems] = useState<TemplateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems((await listTemplates({})).items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { items, loading, initialLoaded, error, refresh };
}
